import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import type { StatusItem } from './kpi.types';
import { formatPercentage } from './kpi.data';

type KpiDonutChartProps = {
    statusItems: StatusItem[];
    chartCenterLabel: string;
};

const KpiDonutChart = ({ statusItems, chartCenterLabel }: KpiDonutChartProps) => {
    const [hoveredSlice, setHoveredSlice] = useState<{ label: string; percent: number } | null>(null);
    const size = 164;
    const strokeWidth = 28;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const segmentGap = 1;
    let cumulativeLength = 0;
    const allZeroSum = statusItems.reduce((sum: number, item: StatusItem) => sum + item.percent, 0) === 0;

    return (
        <Box sx={{ position: 'relative', width: { xs: 120, sm: 140, md: 164 }, height: { xs: 120, sm: 140, md: 164 }, flexShrink: 0 }}>
            <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`}>
                <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                    {statusItems.map((item) => {
                        if (item.percent <= 0) {
                            return null;
                        }

                        const rawLength = (item.percent / 100) * circumference;
                        const dash = Math.max(rawLength - segmentGap, 0);
                        const gap = circumference - dash;
                        const offset = -cumulativeLength;
                        cumulativeLength += rawLength;

                        return (
                            <circle
                                key={item.label}
                                cx={size / 2}
                                cy={size / 2}
                                r={radius}
                                fill="none"
                                stroke={item.color}
                                strokeWidth={strokeWidth}
                                strokeLinecap="butt"
                                strokeDasharray={`${dash} ${gap}`}
                                strokeDashoffset={offset}
                                onMouseEnter={() => setHoveredSlice({ label: item.label, percent: item.percent })}
                                onMouseLeave={() => setHoveredSlice(null)}
                                style={{ cursor: 'pointer' }}
                            />
                        );
                    })}
                </g>
            </svg>
            {hoveredSlice && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -36,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        px: 1,
                        py: 0.5,
                        borderRadius: '4px',
                        background: '#111827',
                        color: '#FFFFFF',
                        fontSize: '11px',
                        whiteSpace: 'nowrap',
                        zIndex: 2,
                        pointerEvents: 'none',
                    }}
                >
                    {hoveredSlice.label}
                </Box>
            )}
            <Typography
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: '16px', sm: '19px', md: '22px' },
                    fontWeight: 700,
                    color: '#1F2937',
                    pointerEvents: 'none',
                }}
            >
                {hoveredSlice ? `${formatPercentage(hoveredSlice.percent)}%` : allZeroSum ? '' : chartCenterLabel}
            </Typography>
        </Box>
    );
};

export default KpiDonutChart;
